import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { RefObject } from 'react'
import { FaxComposeHooks } from '@dataTypes/message.types'
import { RichTextEditorComponent } from '@syncfusion/ej2-react-richtexteditor'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { useGlobalLoadingActions } from '@hooks'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createFaxPageLimitReachedToast,
  createFaxCoversheetTemplateSavedToast,
  createFaxCoversheetTemplateSaveFailedToast,
  createFaxNoRecipientToast,
  createFaxSentToast,
} from '@utils/notificationToast.util'
import {
  addCoversheet,
  addFax,
  fetchAllCoversheets,
  fetchAllFax,
  fetchFaxSettings,
  updateFax,
} from '@store/actions'
import {
  CoversheetCustomFieldType,
  CoversheetFieldSource,
  CoversheetMode,
  IFaxCoversheet,
  IFaxCoversheetContent,
  IFaxDraftComposeState,
  IFaxMessage,
  RecipientDestination,
} from '@dataTypes/fax.type'
import {
  clearFaxComposeSeed,
  setActiveFolder,
} from '@store/reducers/fax.reducer'
import {
  applyTemplateToContent,
  coversheetModeToDb,
  createCoversheetField,
  createEmptyCoversheetContent,
  dbCoversheetModeToCompose,
  getComposePreviewContent,
  htmlToPlainText,
  parseDraftComposeState,
  previewContentToHtml,
  renderCoversheetToHtml,
  renderCoversheetToPlainText,
  resolveRecipientToNumber,
  sanitizeCoversheetContent,
  stringifyCoversheetContent,
  stringifyDraftComposeState,
  syncRecipientToContent,
  getFaxDraftPayloadSizeError,
} from '@utils/fax-coversheet.util'
import { IContact } from '@dataTypes/contact.type'
import { FilterSelection } from '@components/common/SearchWithFilter/SearchWithFilter.component'
import { getClinicFaxLine } from '@utils/fax.util'
import { IClinicNumber } from '@dataTypes/clinic.type'
import {
  createDocument,
  deleteDocumentById,
  getFaxFileSignedUrl,
  updateDocumentById,
  uploadFaxFileToBlob,
} from '@services/api/fax/document.api'
import { getPdfPageCount, isPdfFile } from '@utils/pdf-preview.util'
import { logger } from '@utils/safeLogger'

const FAX_COMPOSE_LOADING_CONTAINER = '.fax-compose-container'
const DRAFT_SAVE_DEBOUNCE_MS = 1500

const matchesCoversheetSearch = (
  coversheet: IFaxCoversheet,
  query: string,
): boolean => {
  const normalized = query.trim().toLowerCase()
  if (!normalized) return true
  return (
    (coversheet.file_name?.toLowerCase().includes(normalized) ?? false) ||
    (coversheet.description?.toLowerCase().includes(normalized) ?? false)
  )
}

const matchesCoversheetTypeFilter = (
  coversheet: IFaxCoversheet,
  filter: FilterSelection,
): boolean => {
  const typeFilter = filter.type
  if (!typeFilter?.length) return true

  const isSystem = coversheet.template_kind === 'system'
  const wantsDefault = typeFilter.includes('Default')
  const wantsCustom = typeFilter.includes('Custom')

  if (wantsDefault && wantsCustom) return true
  if (wantsDefault && isSystem) return true
  if (wantsCustom && !isSystem) return true
  return false
}

const filterCoversheets = (
  coversheets: IFaxCoversheet[],
  query: string,
  filter: FilterSelection,
  kind: 'system' | 'custom',
): IFaxCoversheet[] => {
  const byKind =
    kind === 'system'
      ? coversheets.filter((c) => c.template_kind === 'system')
      : coversheets.filter((c) => c.template_kind !== 'system')

  return byKind
    .filter((c) => matchesCoversheetSearch(c, query))
    .filter((c) => matchesCoversheetTypeFilter(c, filter))
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
}

export type WizardDocumentStatus = 'uploading' | 'uploaded' | 'error'

export type WizardDocument = {
  localId: string
  file_name: string
  mime_type?: string
  file_size?: number
  file?: File
  faxDocumentId?: string
  fileUrl?: string
  pageCount?: number
  status: WizardDocumentStatus
}

function useFaxCompose(
  rteRef?: RefObject<RichTextEditorComponent | null>,
): FaxComposeHooks {
  const dispatch = useAppDispatch()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()

  const coversheets = useAppSelector((state) => state.fax.coversheets)
  const contacts = useAppSelector((state) => state.contacts.data)
  const activeFax = useAppSelector((state) => state.fax.activeFax)
  const activeFolder = useAppSelector((state) => state.fax.activeFolder)
  const composeSeed = useAppSelector((state) => state.fax.composeSeed)
  const clinic = useAppSelector((state) => state.clinic.selectedClinic)
  const faxSettings = useAppSelector((state) => state.fax.settings)

  const [clinicLogoUrl, setClinicLogoUrl] = useState<string | null>(null)
  const clinicLogoUrlRef = useRef<string | null>(null)
  const maxPagesRef = useRef<number | null>(null)

  useEffect(() => {
    clinicLogoUrlRef.current = clinicLogoUrl
  }, [clinicLogoUrl])

  useEffect(() => {
    let cancelled = false
    const rawLogo = faxSettings?.add_clinic_logo
      ? (clinic?.logo_url ?? null)
      : null

    const applyLogo = (url: string | null): void => {
      if (cancelled) return
      setClinicLogoUrl(url)
      clinicLogoUrlRef.current = url
    }

    if (!rawLogo) {
      applyLogo(null)
      return
    }

    if (/[?&]sig=/.test(rawLogo)) {
      applyLogo(rawLogo)
      return
    }

    getFaxFileSignedUrl(rawLogo)
      .then((res) => applyLogo(res.data?.url ?? null))
      .catch(() => applyLogo(null))

    return () => {
      cancelled = true
    }
  }, [clinic?.logo_url, faxSettings?.add_clinic_logo])

  useEffect(() => {
    maxPagesRef.current = faxSettings?.max_pages ?? null
  }, [faxSettings])

  useEffect(() => {
    if (!faxSettings) {
      dispatch(fetchFaxSettings())
    }
  }, [])

  const draftSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const draftFaxIdRef = useRef<string | null>(activeFax?.fax_message_id ?? null)
  const ensureDraftInFlightRef = useRef<Promise<string | null> | null>(null)
  const uploadInFlightRef = useRef<Map<string, Promise<void>>>(new Map())
  const sortOrderTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hydratedRef = useRef(false)
  const composeActiveRef = useRef(Boolean(activeFax?.status === 'draft'))
  const sendingRef = useRef(false)
  const seedAppliedRef = useRef(false)
  const [activePreviewLoads, setActivePreviewLoads] = useState(0)
  const previewLoaderShownRef = useRef(false)

  const beginPreviewLoading = useCallback(() => {
    setActivePreviewLoads((count) => count + 1)
  }, [])

  const endPreviewLoading = useCallback(() => {
    setActivePreviewLoads((count) => Math.max(0, count - 1))
  }, [])

  useEffect(() => {
    if (activePreviewLoads > 0) {
      previewLoaderShownRef.current = true
      showLoading({ container: FAX_COMPOSE_LOADING_CONTAINER })
    } else if (previewLoaderShownRef.current) {
      previewLoaderShownRef.current = false
      hideLoading(FAX_COMPOSE_LOADING_CONTAINER)
    }
  }, [activePreviewLoads, showLoading, hideLoading])

  const [step, setStep] = useState<number>(0)
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null,
  )
  const [coversheetMode, setCoversheetMode] =
    useState<CoversheetMode>('template')
  const [uploadedCoversheetFile, setUploadedCoversheetFile] =
    useState<File | null>(null)
  const [coverContent, setCoverContent] = useState<IFaxCoversheetContent>(
    createEmptyCoversheetContent(),
  )
  const [recipient, setRecipient] = useState<IContact | null>(null)
  const [recipientDestination, setRecipientDestination] =
    useState<RecipientDestination>('fax_number')
  const [documents, setDocuments] = useState<WizardDocument[]>([])
  const documentsRef = useRef<WizardDocument[]>([])
  const [coverPageCount, setCoverPageCount] = useState<number>(0)
  const coverPageCountRef = useRef<number>(0)
  const [draftFaxId, setDraftFaxId] = useState<string | null>(
    activeFax?.fax_message_id ?? null,
  )
  const [showRecipientModal, setShowRecipientModal] = useState<boolean>(false)
  const [showInsertFieldModal, setShowInsertFieldModal] =
    useState<boolean>(false)
  const [coversheetSearchQuery, setCoversheetSearchQuery] = useState<string>('')
  const [coversheetFilter, setCoversheetFilter] = useState<FilterSelection>({})

  useEffect(() => {
    draftFaxIdRef.current = draftFaxId
  }, [draftFaxId])

  useEffect(() => {
    documentsRef.current = documents
  }, [documents])

  useEffect(() => {
    coverPageCountRef.current = coverPageCount
  }, [coverPageCount])

  useEffect(() => {
    let cancelled = false
    const computeCoverPages = async (): Promise<void> => {
      if (coversheetMode === 'template') {
        if (!cancelled) setCoverPageCount(1)
        return
      }
      if (coversheetMode === 'uploaded') {
        const pages = uploadedCoversheetFile
          ? isPdfFile(uploadedCoversheetFile.type, uploadedCoversheetFile.name)
            ? await getPdfPageCount(uploadedCoversheetFile)
            : 1
          : 1
        if (!cancelled) setCoverPageCount(pages)
        return
      }
      if (!cancelled) setCoverPageCount(0)
    }
    void computeCoverPages()
    return () => {
      cancelled = true
    }
  }, [coversheetMode, uploadedCoversheetFile])

  const getTotalPageCount = useCallback((): number => {
    const docPages = documentsRef.current.reduce(
      (sum, doc) => sum + (doc.pageCount ?? 1),
      0,
    )
    return docPages + coverPageCountRef.current
  }, [])

  const uploadingDocuments = useMemo(
    () => documents.some((d) => d.status === 'uploading'),
    [documents],
  )

  const clinicFaxLine = useMemo(
    () => getClinicFaxLine(clinic?.clinic_number ?? []),
    [clinic?.clinic_number],
  )

  const systemTemplates = useMemo(
    () =>
      filterCoversheets(
        coversheets,
        coversheetSearchQuery,
        coversheetFilter,
        'system',
      ),
    [coversheets, coversheetSearchQuery, coversheetFilter],
  )

  const handleFilterChange = useCallback(
    (selectedFilters: FilterSelection): void => {
      setCoversheetFilter(selectedFilters)
    },
    [],
  )

  const customTemplates = useMemo(
    () =>
      filterCoversheets(
        coversheets,
        coversheetSearchQuery,
        coversheetFilter,
        'custom',
      ),
    [coversheets, coversheetSearchQuery, coversheetFilter],
  )

  const recipientActionLabel = useMemo(
    () => (recipient ? 'Change recipient' : 'Select recipient'),
    [recipient],
  )

  const showDefaultCoversheets = useMemo(() => {
    const typeFilter = coversheetFilter.type
    if (!typeFilter?.length) return true
    return typeFilter.includes('Default')
  }, [coversheetFilter.type])

  const showCustomCoversheets = useMemo(() => {
    const typeFilter = coversheetFilter.type
    if (!typeFilter?.length) return true
    return typeFilter.includes('Custom')
  }, [coversheetFilter.type])

  const clinicCtx = useMemo(
    () => ({
      clinicLine: clinicFaxLine as IClinicNumber,
      clinicName: clinic?.clinic_name,
      recipient,
    }),
    [clinicFaxLine, clinic?.clinic_name, recipient],
  )

  const previewContent = useMemo(
    () => getComposePreviewContent(coversheetMode, coverContent, clinicLogoUrl),
    [coversheetMode, coverContent, clinicLogoUrl],
  )

  const previewHtml = useMemo(
    () => previewContentToHtml(previewContent),
    [previewContent],
  )

  const applyClinicFromFields = useCallback(
    (content: IFaxCoversheetContent): IFaxCoversheetContent => ({
      ...content,
      from: {
        displayName: clinic?.clinic_name || content.from?.displayName || '-',
        faxNumber:
          clinicFaxLine?.phone_number ?? content.from?.faxNumber ?? '-',
      },
    }),
    [clinic, clinicFaxLine],
  )

  useEffect(() => {
    if (!clinic || step !== 1 || coversheetMode !== 'template') return
    setCoverContent((prev) => applyClinicFromFields(prev))
  }, [clinic, clinicFaxLine, step, coversheetMode, applyClinicFromFields])

  const buildDraftComposeState = useCallback((): IFaxDraftComposeState => {
    return {
      version: 1,
      step,
      coversheetMode,
      selectedTemplateId: selectedTemplateId ?? undefined,
      coverContent: sanitizeCoversheetContent(coverContent),
      contactId: recipient?.contact_id,
      recipientDestination,
    }
  }, [
    step,
    coversheetMode,
    selectedTemplateId,
    coverContent,
    recipient,
    recipientDestination,
  ])

  const ensureDraftFax = useCallback(async (): Promise<string | null> => {
    if (draftFaxIdRef.current) return draftFaxIdRef.current
    if (ensureDraftInFlightRef.current) {
      return ensureDraftInFlightRef.current
    }

    const createPromise = (async (): Promise<string | null> => {
      if (draftFaxIdRef.current) return draftFaxIdRef.current

      const toNumber = resolveRecipientToNumber(recipient)
      const fromNumber = clinicFaxLine?.phone_number || ''
      try {
        const result = await dispatch(
          addFax({
            fax: {
              clinic_id: '',
              direction: 'Outgoing',
              intent: 'create_draft',
              to_number: toNumber,
              from_number: fromNumber,
              contact_id: recipient?.contact_id ?? null,
              subject: coverContent.title || 'Fax',
              coversheet_mode: coversheetModeToDb(coversheetMode),
              draft_compose_state: stringifyDraftComposeState(
                buildDraftComposeState(),
              ),
            },
          }),
        ).unwrap()
        const id = (result as IFaxMessage | undefined)?.fax_message_id
        if (id) {
          setDraftFaxId(id)
          draftFaxIdRef.current = id
        }
        return id ?? null
      } catch {
        return null
      } finally {
        ensureDraftInFlightRef.current = null
      }
    })()

    ensureDraftInFlightRef.current = createPromise
    return createPromise
  }, [
    recipient,
    clinicFaxLine,
    dispatch,
    coverContent.title,
    coversheetMode,
    buildDraftComposeState,
  ])

  const buildDraftSavePayload = useCallback((): Partial<IFaxMessage> => {
    const toNumber = resolveRecipientToNumber(recipient)
    const fromNumber = clinicFaxLine?.phone_number || ''
    const draftState = stringifyDraftComposeState(buildDraftComposeState())
    const cleanContent = sanitizeCoversheetContent(coverContent)
    const snapshotHtml =
      coversheetMode === 'template'
        ? renderCoversheetToHtml(cleanContent, clinicLogoUrlRef.current)
        : ''
    const snapshotText =
      coversheetMode === 'template'
        ? renderCoversheetToPlainText(cleanContent)
        : ''

    return {
      to_number: toNumber,
      from_number: fromNumber,
      contact_id: recipient?.contact_id ?? null,
      fax_coversheet_id: selectedTemplateId,
      subject: coverContent.title || 'Fax',
      coversheet_mode: coversheetModeToDb(coversheetMode),
      draft_compose_state: draftState,
      cover_snapshot_html: snapshotHtml || null,
      cover_snapshot_text: snapshotText || null,
      page_count: getTotalPageCount(),
      intent: 'save_draft',
    }
  }, [
    recipient,
    clinicFaxLine,
    buildDraftComposeState,
    coversheetMode,
    coverContent,
    selectedTemplateId,
    getTotalPageCount,
  ])

  const saveDraft = useCallback(
    async (options?: { silent?: boolean }): Promise<boolean> => {
      if (!composeActiveRef.current) return true
      if (sendingRef.current) return true

      const payload = buildDraftSavePayload()
      const sizeError = getFaxDraftPayloadSizeError({
        ...payload,
        clinic_id: clinic?.clinic_id ?? '',
      })
      if (sizeError) {
        if (!options?.silent) {
          showToast(createFailedToast('Cover sheet too large', sizeError))
        }
        return false
      }

      try {
        const id = draftFaxIdRef.current ?? (await ensureDraftFax())
        if (!id) {
          if (!options?.silent) {
            showToast(
              createFailedToast('Save failed', 'Could not save fax draft.'),
            )
          }
          return false
        }

        await dispatch(
          updateFax({
            fax_message_id: id,
            data: payload,
          }),
        ).unwrap()
        return true
      } catch {
        if (!options?.silent) {
          showToast(
            createFailedToast(
              'Save failed',
              'Could not save fax draft. Try removing pasted images or attachments.',
            ),
          )
        }
        return false
      }
    },
    [
      buildDraftSavePayload,
      clinic?.clinic_id,
      dispatch,
      ensureDraftFax,
      showToast,
    ],
  )

  const scheduleDraftSave = useCallback(() => {
    if (draftSaveTimerRef.current) {
      clearTimeout(draftSaveTimerRef.current)
    }
    draftSaveTimerRef.current = setTimeout(() => {
      void saveDraft({ silent: true })
    }, DRAFT_SAVE_DEBOUNCE_MS)
  }, [saveDraft])

  const flushDraftSave = useCallback(
    async (options?: { silent?: boolean }): Promise<boolean> => {
      if (!composeActiveRef.current) return true
      if (draftSaveTimerRef.current) {
        clearTimeout(draftSaveTimerRef.current)
        draftSaveTimerRef.current = null
      }
      return saveDraft(options)
    },
    [saveDraft],
  )

  const markComposeActive = useCallback(() => {
    composeActiveRef.current = true
  }, [])

  useEffect(() => {
    if (!composeActiveRef.current) return
    scheduleDraftSave()
  }, [
    step,
    coversheetMode,
    selectedTemplateId,
    coverContent,
    recipient,
    documents,
    scheduleDraftSave,
  ])

  const prevFolderRef = useRef(activeFolder)
  const prevComposeEntryRef = useRef(activeFolder)

  useEffect(() => {
    const enteredCompose =
      prevComposeEntryRef.current !== 'compose' && activeFolder === 'compose'
    prevComposeEntryRef.current = activeFolder

    if (enteredCompose && !activeFax) {
      hydratedRef.current = false
      composeActiveRef.current = false
      sendingRef.current = false
      setStep(0)
      setCoversheetMode('skipped')
      setSelectedTemplateId(null)
      setUploadedCoversheetFile(null)
      setCoverContent(createEmptyCoversheetContent())
      setRecipient(null)
      setRecipientDestination('fax_number')
      setDocuments([])
      setDraftFaxId(null)
      draftFaxIdRef.current = null
      ensureDraftInFlightRef.current = null
    }
  }, [activeFolder, activeFax])

  // Apply a reply / forward seed when entering a fresh compose session.
  useEffect(() => {
    if (activeFolder !== 'compose') {
      seedAppliedRef.current = false
      return
    }
    if (!composeSeed || activeFax || seedAppliedRef.current) return

    seedAppliedRef.current = true
    hydratedRef.current = true
    composeActiveRef.current = true
    setStep(0)
    setCoversheetMode(composeSeed.coversheetMode ?? 'skipped')
    const seededContent =
      composeSeed.coverContent ?? createEmptyCoversheetContent()
    setCoverContent({
      ...seededContent,
      title: composeSeed.subject ?? seededContent.title,
    })
    if (composeSeed.contactId) {
      const contact = contacts.find(
        (c) => c.contact_id === composeSeed.contactId,
      )
      if (contact) {
        setRecipient(contact)
        setRecipientDestination('fax_number')
      }
    }
    dispatch(clearFaxComposeSeed())
  }, [activeFolder, activeFax, composeSeed, contacts, dispatch])

  useEffect(() => {
    if (prevFolderRef.current === 'compose' && activeFolder !== 'compose') {
      void flushDraftSave()
    }
    prevFolderRef.current = activeFolder
  }, [activeFolder, flushDraftSave])

  useEffect(() => {
    return () => {
      void flushDraftSave()
    }
  }, [flushDraftSave])

  useEffect(() => {
    const onBeforeUnload = (): void => {
      void flushDraftSave()
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [flushDraftSave])

  useEffect(() => {
    if (hydratedRef.current) return
    if (activeFax?.status !== 'draft') return
    if (activeFolder !== 'compose') return

    hydratedRef.current = true
    composeActiveRef.current = true
    sendingRef.current = false
    if (activeFax.fax_message_id) {
      setDraftFaxId(activeFax.fax_message_id)
      draftFaxIdRef.current = activeFax.fax_message_id
    }
    const parsed =
      parseDraftComposeState(activeFax.draft_compose_state) ??
      (activeFax.coversheet_mode
        ? {
            version: 1 as const,
            step: 0,
            coversheetMode: dbCoversheetModeToCompose(
              activeFax.coversheet_mode,
            ),
          }
        : null)

    if (parsed) {
      setStep(parsed.step)
      setCoversheetMode(parsed.coversheetMode)
      if (parsed.selectedTemplateId) {
        setSelectedTemplateId(parsed.selectedTemplateId)
      }
      if (parsed.recipientDestination) {
        setRecipientDestination(parsed.recipientDestination)
      }
      if (parsed.coverContent) {
        setCoverContent(parsed.coverContent)
      }
    }

    if (activeFax.documents?.length) {
      const restored: WizardDocument[] = [...activeFax.documents]
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
        .map((doc) => ({
          localId: doc.fax_document_id ?? `${Date.now()}-${doc.file_url}`,
          file_name: doc.file_name ?? 'Document',
          mime_type: doc.mime_type ?? undefined,
          file_size: doc.file_size ?? undefined,
          faxDocumentId: doc.fax_document_id,
          fileUrl: doc.file_url,
          status: 'uploaded',
        }))
      setDocuments(restored)

      void (async () => {
        const counts = await Promise.all(
          restored.map(async (doc) => {
            if (!isPdfFile(doc.mime_type, doc.file_name) || !doc.fileUrl) {
              return { localId: doc.localId, pageCount: 1 }
            }
            const signed = await getFaxFileSignedUrl(doc.fileUrl)
            const url = signed.data?.url
            const pageCount = url ? await getPdfPageCount(url) : 1
            return { localId: doc.localId, pageCount }
          }),
        )
        setDocuments((prev) =>
          prev.map((d) => {
            const match = counts.find((c) => c.localId === d.localId)
            return match ? { ...d, pageCount: match.pageCount } : d
          }),
        )
      })()
    }

    if (activeFax.contact_id && contacts.length) {
      const contact = contacts.find(
        (c) => c.contact_id === activeFax.contact_id,
      )
      if (contact) setRecipient(contact)
    }
  }, [activeFax, activeFolder, contacts])

  const selectTemplate = useCallback(
    (template: IFaxCoversheet | null) => {
      if (!template) {
        setSelectedTemplateId(null)
        setCoverContent(createEmptyCoversheetContent())
        return
      }
      markComposeActive()
      setSelectedTemplateId(template.fax_coversheet_id ?? null)
      setCoverContent(
        applyClinicFromFields(applyTemplateToContent(template, clinicCtx)),
      )
      setCoversheetMode('template')
      setUploadedCoversheetFile(null)
      setStep(1)
    },
    [applyClinicFromFields, clinicCtx, markComposeActive],
  )

  const handleCreateNewTemplate = useCallback(() => {
    markComposeActive()
    setSelectedTemplateId(null)
    setCoverContent(
      applyClinicFromFields(createEmptyCoversheetContent('New cover sheet')),
    )
    setCoversheetMode('template')
    setUploadedCoversheetFile(null)
    setStep(1)
  }, [applyClinicFromFields, markComposeActive])

  const handleSkipCover = useCallback(() => {
    markComposeActive()
    setCoversheetMode('skipped')
    setUploadedCoversheetFile(null)
    setSelectedTemplateId(null)
    setStep(2)
  }, [markComposeActive])

  const handleUploadCoverSheet = useCallback(
    async (file: File): Promise<void> => {
      const isAllowedType =
        file.type === 'application/pdf' ||
        file.type.startsWith('image/') ||
        /\.(pdf|png|jpe?g|webp)$/i.test(file.name)

      if (!isAllowedType) {
        showToast(
          createFailedToast(
            'Invalid file',
            'Upload a PDF or image cover sheet.',
          ),
        )
        return
      }

      markComposeActive()
      setUploadedCoversheetFile(file)
      setCoversheetMode('uploaded')
      setSelectedTemplateId(null)
      setStep(2)
    },
    [markComposeActive, showToast],
  )

  const handleNext = useCallback(async (): Promise<void> => {
    const maxPages = maxPagesRef.current
    if (maxPages && maxPages > 0) {
      const totalPages = getTotalPageCount()
      if (totalPages > maxPages) {
        showToast(createFaxPageLimitReachedToast(totalPages, maxPages))
        return
      }
    }

    if (composeActiveRef.current) {
      const saved = await flushDraftSave({ silent: false })
      if (!saved) return
    }

    setStep(step + 1)
  }, [step, getTotalPageCount, showToast, flushDraftSave])

  const handleBack = useCallback(() => {
    setStep((s) => {
      if (s === 1) {
        setCoversheetMode('skipped')
        setSelectedTemplateId(null)
        setUploadedCoversheetFile(null)
        return 0
      }
      if (s === 2 && coversheetMode === 'skipped') return 0
      return Math.max(0, s - 1)
    })
  }, [coversheetMode])

  const insertField = useCallback(
    (base: CoversheetCustomFieldType, source: CoversheetFieldSource) => {
      const field = createCoversheetField(base, source, clinicCtx)
      setCoverContent((prev) => ({
        ...prev,
        fields: [...prev.fields, field],
      }))
      setShowInsertFieldModal(false)
    },
    [clinicCtx],
  )

  const handleMessageChange = useCallback(
    (html: string, plainText?: string) => {
      const text = plainText ?? htmlToPlainText(html)
      setCoverContent((prev) => ({
        ...prev,
        messageHtml: html,
        messageText: text,
      }))
    },
    [],
  )

  const handleSaveTemplate = useCallback(async () => {
    try {
      showLoading({ container: FAX_COMPOSE_LOADING_CONTAINER })
      const contentWithText = {
        ...coverContent,
        messageText:
          coverContent.messageText ??
          htmlToPlainText(coverContent.messageHtml ?? ''),
      }
      const templateSizeError = getFaxDraftPayloadSizeError({
        content: stringifyCoversheetContent(contentWithText),
        clinic_id: clinic?.clinic_id ?? '',
      })
      if (templateSizeError) {
        showToast(createFailedToast('Cover sheet too large', templateSizeError))
        return
      }
      await dispatch(
        addCoversheet({
          clinic_id: clinic?.clinic_id ?? '',
          file_name: coverContent.title || 'Custom template',
          content: stringifyCoversheetContent(contentWithText),
          template_kind: 'custom',
          description: 'Custom cover sheet template',
        }),
      ).unwrap()
      showToast(createFaxCoversheetTemplateSavedToast())
    } catch {
      showToast(createFaxCoversheetTemplateSaveFailedToast())
    } finally {
      hideLoading(FAX_COMPOSE_LOADING_CONTAINER)
    }
  }, [
    coverContent,
    clinic?.clinic_id,
    dispatch,
    hideLoading,
    showLoading,
    showToast,
  ])

  const persistDocumentSortOrder = useCallback(() => {
    if (sortOrderTimerRef.current) {
      clearTimeout(sortOrderTimerRef.current)
    }
    sortOrderTimerRef.current = setTimeout(() => {
      const snapshot = documentsRef.current
      snapshot.forEach((doc, index) => {
        if (doc.faxDocumentId && doc.status === 'uploaded') {
          void updateDocumentById(doc.faxDocumentId, { sort_order: index })
        }
      })
    }, DRAFT_SAVE_DEBOUNCE_MS)
  }, [])

  const addLocalDocument = useCallback(
    async (file: File): Promise<void> => {
      markComposeActive()
      const localId = `${Date.now()}-${Math.random().toString(36).slice(2)}-${file.name}`

      setDocuments((prev) => [
        ...prev,
        {
          localId,
          file_name: file.name,
          mime_type: file.type || 'application/octet-stream',
          file_size: file.size,
          file,
          status: 'uploading',
        },
      ])

      const uploadPromise = (async (): Promise<void> => {
        beginPreviewLoading()
        const pageCountPromise = isPdfFile(file.type, file.name)
          ? getPdfPageCount(file).catch(() => 1)
          : Promise.resolve(1)
        try {
          const faxId = await ensureDraftFax()
          if (!faxId) {
            throw new Error('Could not create draft fax.')
          }

          const uploadRes = await uploadFaxFileToBlob(file)
          if (uploadRes.statusCode >= 400 || !uploadRes.data?.file_url) {
            throw new Error(uploadRes.message || 'Upload failed.')
          }

          const sortOrder = documentsRef.current.findIndex(
            (d) => d.localId === localId,
          )
          const docRes = await createDocument({
            fax_message_id: faxId,
            file_url: uploadRes.data.file_url,
            file_name: uploadRes.data.file_name,
            mime_type: uploadRes.data.mime_type,
            file_size: uploadRes.data.file_size,
            sort_order: sortOrder < 0 ? 0 : sortOrder,
            clinic_id: clinic?.clinic_id ?? '',
          })

          const created = docRes.data?.fax_document
          if (docRes.statusCode >= 400 || !created?.fax_document_id) {
            throw new Error(docRes.message || 'Could not save fax document.')
          }

          const pageCount = await pageCountPromise

          setDocuments((prev) =>
            prev.map((d) =>
              d.localId === localId
                ? {
                    ...d,
                    faxDocumentId: created.fax_document_id,
                    fileUrl: uploadRes.data.file_url,
                    pageCount,
                    status: 'uploaded',
                  }
                : d,
            ),
          )
        } catch (error) {
          logger.error('Failed to attach fax document:', error)
          setDocuments((prev) =>
            prev.map((d) =>
              d.localId === localId ? { ...d, status: 'error' } : d,
            ),
          )
          showToast(
            createFailedToast(
              'Attachment failed',
              `Could not attach ${file.name}. Remove it and try again.`,
            ),
          )
        } finally {
          endPreviewLoading()
          uploadInFlightRef.current.delete(localId)
        }
      })()

      uploadInFlightRef.current.set(localId, uploadPromise)
      await uploadPromise
    },
    [
      markComposeActive,
      ensureDraftFax,
      beginPreviewLoading,
      endPreviewLoading,
      clinic?.clinic_id,
      showToast,
    ],
  )

  const removeDocument = useCallback(async (localId: string): Promise<void> => {
    const inFlight = uploadInFlightRef.current.get(localId)
    if (inFlight) {
      try {
        await inFlight
      } catch {
        // upload failed; nothing persisted to clean up
      }
    }

    const target = documentsRef.current.find((d) => d.localId === localId)

    setDocuments((prev) => prev.filter((d) => d.localId !== localId))

    if (target?.faxDocumentId) {
      try {
        await deleteDocumentById(target.faxDocumentId)
      } catch (error) {
        logger.error('Failed to delete fax document:', error)
      }
    }
  }, [])

  const reorderDocuments = useCallback(
    (fromIndex: number, toIndex: number): void => {
      setDocuments((prev) => {
        if (
          fromIndex === toIndex ||
          fromIndex < 0 ||
          toIndex < 0 ||
          fromIndex >= prev.length ||
          toIndex >= prev.length
        ) {
          return prev
        }
        const next = [...prev]
        const [moved] = next.splice(fromIndex, 1)
        next.splice(toIndex, 0, moved)
        return next
      })
      persistDocumentSortOrder()
    },
    [persistDocumentSortOrder],
  )

  const handleSend = useCallback(async () => {
    const toNumber = resolveRecipientToNumber(recipient)
    if (!recipient || !toNumber.trim()) {
      showToast(createFaxNoRecipientToast())
      return
    }
    const fromNumber = clinicFaxLine?.phone_number || ''
    const cleanContent = sanitizeCoversheetContent(coverContent)
    const snapshotHtml =
      coversheetMode === 'template'
        ? renderCoversheetToHtml(cleanContent, clinicLogoUrlRef.current)
        : ''
    const snapshotText =
      coversheetMode === 'template'
        ? renderCoversheetToPlainText(cleanContent)
        : ''

    const pageCount = getTotalPageCount()

    const sendPayload: Partial<IFaxMessage> = {
      direction: 'Outgoing',
      intent: 'send',
      to_number: toNumber,
      from_number: fromNumber,
      contact_id: recipient?.contact_id ?? null,
      fax_coversheet_id: selectedTemplateId,
      cover_snapshot_html: snapshotHtml || null,
      cover_snapshot_text: snapshotText || null,
      coversheet_mode: coversheetModeToDb(coversheetMode),
      draft_compose_state: null,
      subject: coverContent.title || 'Fax',
      page_count: pageCount,
    }

    const sendSizeError = getFaxDraftPayloadSizeError({
      ...sendPayload,
      clinic_id: clinic?.clinic_id ?? '',
    })
    if (sendSizeError) {
      showToast(createFailedToast('Cover sheet too large', sendSizeError))
      return
    }

    try {
      showLoading({ container: FAX_COMPOSE_LOADING_CONTAINER })

      sendingRef.current = true
      if (draftSaveTimerRef.current) {
        clearTimeout(draftSaveTimerRef.current)
        draftSaveTimerRef.current = null
      }

      let faxId = draftFaxIdRef.current
      if (!faxId) {
        faxId = await ensureDraftFax()
      }

      if (coversheetMode === 'uploaded' && uploadedCoversheetFile) {
        const coverUpload = await uploadFaxFileToBlob(uploadedCoversheetFile)
        if (coverUpload.statusCode >= 400 || !coverUpload.data?.file_url) {
          sendingRef.current = false
          showToast(
            createFailedToast(
              'Upload failed',
              coverUpload.message || 'Could not upload cover sheet.',
            ),
          )
          return
        }
      }

      const faxPayload = sendPayload

      if (faxId) {
        await dispatch(
          updateFax({
            fax_message_id: faxId,
            data: faxPayload,
          }),
        ).unwrap()
      } else {
        const created = await dispatch(
          addFax({
            fax: faxPayload as IFaxMessage,
          }),
        ).unwrap()
        faxId = (created as IFaxMessage | undefined)?.fax_message_id ?? null
      }

      if (faxId) {
        await Promise.allSettled(Array.from(uploadInFlightRef.current.values()))

        let sortOrder = 0
        for (const doc of documentsRef.current) {
          if (doc.faxDocumentId) {
            await updateDocumentById(doc.faxDocumentId, {
              sort_order: sortOrder,
            })
            sortOrder += 1
            continue
          }

          if (!doc.file) {
            sortOrder += 1
            continue
          }

          const uploadRes = await uploadFaxFileToBlob(doc.file)
          if (uploadRes.statusCode >= 400 || !uploadRes.data?.file_url) {
            showToast(
              createFailedToast(
                'Upload failed',
                uploadRes.message || `Could not upload ${doc.file_name}.`,
              ),
            )
            return
          }
          await createDocument({
            fax_message_id: faxId,
            file_url: uploadRes.data.file_url,
            file_name: uploadRes.data.file_name,
            mime_type: uploadRes.data.mime_type,
            file_size: uploadRes.data.file_size,
            sort_order: sortOrder,
            clinic_id: clinic?.clinic_id ?? '',
          })
          sortOrder += 1
        }
      }

      await dispatch(fetchAllFax())
      showToast(createFaxSentToast())
      dispatch(setActiveFolder('sent'))
    } catch {
      sendingRef.current = false
      showToast(createFailedToast('Send failed', 'Could not send fax.'))
    } finally {
      hideLoading(FAX_COMPOSE_LOADING_CONTAINER)
    }
  }, [
    recipient,
    clinicFaxLine,
    coversheetMode,
    coverContent,
    selectedTemplateId,
    uploadedCoversheetFile,
    ensureDraftFax,
    dispatch,
    showLoading,
    hideLoading,
    showToast,
    clinic?.clinic_id,
    getTotalPageCount,
  ])

  const setRecipientContact = useCallback(
    (contact: IContact, destination: RecipientDestination = 'fax_number') => {
      setRecipient(contact)
      setRecipientDestination(destination)
      setCoverContent((prev) =>
        syncRecipientToContent(prev, contact, destination),
      )
      setShowRecipientModal(false)
      markComposeActive()
    },
    [markComposeActive],
  )

  useEffect(() => {
    dispatch(fetchAllCoversheets())
  }, [dispatch, clinic])

  useEffect(() => {
    return () => {
      if (sortOrderTimerRef.current) {
        clearTimeout(sortOrderTimerRef.current)
      }
    }
  }, [])

  return {
    coversheetSearchQuery,
    coversheetFilter,
    handleFilterChange,
    setCoversheetSearchQuery,
    step,
    systemTemplates,
    customTemplates,
    showDefaultCoversheets,
    showCustomCoversheets,
    selectedTemplateId,
    selectTemplate,
    coversheetMode,
    uploadedCoversheetFile,
    coverContent,
    setCoverContent,
    recipient,
    recipientDestination,
    recipientActionLabel,
    documents,
    showRecipientModal,
    setShowRecipientModal,
    showInsertFieldModal,
    setShowInsertFieldModal,
    handleCreateNewTemplate,
    handleSkipCover,
    handleUploadCoverSheet,
    handleNext,
    handleBack,
    insertField,
    handleSaveTemplate,
    handleMessageChange,
    addLocalDocument,
    uploadingDocuments,
    removeDocument,
    reorderDocuments,
    beginPreviewLoading,
    endPreviewLoading,
    ensureDraftFax,
    handleSend,
    setRecipientContact,
    contacts,
    previewHtml,
    previewContent,
    rteRef,
  }
}

export default useFaxCompose
